/**
 * Toast.js - Floating notification component
 * 
 * Shows a temporary message that appears at the bottom of the screen.
 * Auto-dismisses after a set duration.
 * 
 * Usage:
 *   <Toast 
 *     message="Saved successfully!" 
 *     type="success" 
 *     onClose={() => setShowToast(false)} 
 *   />
 */

import React, { useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

function Toast({ message, type = 'success', duration = 3000, onClose }) {
  const { colors, darkMode } = useTheme();

  // Auto-dismiss after duration
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  // Get background color based on type
  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return colors.green;
      case 'error':
        return colors.red;
      case 'info':
        return colors.blue;
      default:
        return colors.green;
    }
  };

  const toastStyle = {
    position: 'fixed',
    top: '70px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: getBackgroundColor(),
    color: darkMode ? '#1a1a2e' : '#fff',
    padding: '14px 24px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 9999,
    fontWeight: '600',
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    animation: 'slideDown 0.3s ease',
    maxWidth: '90vw',
    textAlign: 'center'
  };

  const iconStyle = {
    fontSize: '1.2rem'
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'info':
        return 'ℹ';
      default:
        return '✓';
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
      <div style={toastStyle} role="alert">
        <span style={iconStyle}>{getIcon()}</span>
        <span>{message}</span>
      </div>
    </>
  );
}

export default Toast;
